import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'

export const ProfileHeaderLayout = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>(({ isMobile }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: isMobile ? 20 : 24,
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

export const ProfileIdentity = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>(({ isMobile }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexDirection: isMobile ? 'column' : 'row',
  textAlign: isMobile ? 'center' : 'left',
}))

export const AvatarImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
})

export const StatsGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>(({ isMobile }) => ({
  display: 'grid',
  gridTemplateColumns: isMobile
    ? 'repeat(2, minmax(0, 1fr))'
    : 'repeat(6, auto)',
  gap: 10,
  justifyContent: 'center',
  width: isMobile ? '100%' : 'auto',
  marginTop: isMobile ? 8 : 0,
}))

export const StatChip = styled(Box)(({ theme }) => ({
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid',
  borderColor: theme.palette.divider,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  fontSize: 13,
}))

export const StatValue = styled(Typography)({
  fontWeight: 600,
})

export const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}))

export const BioText = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.primary,
  marginTop: theme.spacing(0.75),
  maxWidth: 520,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
}))

export const BadgesRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}))

// Milestone chip; the description shows as a native tooltip via title.
export const BadgeChip = styled(Box)(({ theme }) => ({
  padding: '4px 12px',
  borderRadius: 999,
  backgroundColor: theme.palette.brand.main,
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'default',
}))

export const GenreChipsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
}))

export const GenreChip = styled(Box)(({ theme }) => ({
  padding: '4px 12px',
  borderRadius: 999,
  border: `1px solid ${theme.palette.brand.main}`,
  color: theme.palette.brand.main,
  fontSize: 12,
  fontWeight: 600,
}))

export const SectionBlock = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}))

export const SectionHeadingRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}))

// Taste match between the viewer and this profile, e.g. "82% match".
export const MatchChip = styled(Box)(({ theme }) => ({
  padding: '3px 10px',
  borderRadius: 999,
  backgroundColor: theme.palette.brand.main,
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
}))

// Horizontal poster strip (recently watched, shared titles).
export const HorizontalStrip = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  overflowX: 'auto',
  paddingBottom: theme.spacing(1),
}))

// Expands the capped tab grid to the full list.
export const ShowAllItemsButton = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.brand.main,
  cursor: 'pointer',
  textAlign: 'center',
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  '&:hover': {
    textDecoration: 'underline',
  },
}))

// "This profile is private" notice under the header.
export const PrivateNoticeBox = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}))

export const CommentsListBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}))

export const CommentCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: `calc(${theme.shape.borderRadius}px * 1.5)`,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}))

export const CommentHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
  marginBottom: theme.spacing(0.5),
}))

// Real anchor so right-click / cmd+click open the media page in a new tab.
export const CommentMediaLink = styled(RouterLink)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.text.primary,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.brand.main,
  },
}))

export const CommentDateText = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}))

export const CommentContentText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.primary,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
}))
